import { datadogRum } from '@datadog/browser-rum';
import React, { createContext, useEffect, useRef, useState } from 'react';
import GetAssignedBucket from '|/helpers/experiment/services';

const BACKSTOP_EXPERIMENT = 'pp-backstop';

// Ramp date is 2pm Arizona on 2026-07-30 — only carts started after it bucket. Arizona is MST
// year-round (UTC-7, no DST) and the isEligible comparison is epoch-based, so the offset is baked
// into the literal. The placeholder flag is DERIVED from the date so the merge-blocker test can
// catch the testing placeholder ever being shipped.
const BACKSTOP_PLACEHOLDER_START_DATE = new Date('2020-01-01T00:00:00Z');
const BACKSTOP_EXPERIMENT_START_DATE = new Date('2026-07-30T21:00:00Z');
const BACKSTOP_START_DATE_IS_TEST_PLACEHOLDER =
  BACKSTOP_EXPERIMENT_START_DATE.getTime() === BACKSTOP_PLACEHOLDER_START_DATE.getTime();

interface BackstopContextValue {
  isBackstopTreatment: boolean;
}

const BackstopContext = createContext<BackstopContextValue>({
  isBackstopTreatment: false
});

const isEligible = (cartCreatedDateTimeUtc: string | null): boolean => {
  if (!cartCreatedDateTimeUtc) return false;
  const startedAt = new Date(cartCreatedDateTimeUtc).getTime();
  return !Number.isNaN(startedAt) && startedAt >= BACKSTOP_EXPERIMENT_START_DATE.getTime();
};

const BackstopProvider: React.FC<
  React.PropsWithChildren<{ cartCreatedDateTimeUtc: string | null | undefined }>
> = ({ cartCreatedDateTimeUtc, children }) => {
  const [isBackstopTreatment, setIsBackstopTreatment] = useState(false);
  // Which cart we last decided for — re-decide only if summary refreshes to a different cart.
  const decidedForRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    // undefined = summary not loaded yet (wait); null/string = loaded.
    if (cartCreatedDateTimeUtc === undefined || decidedForRef.current === cartCreatedDateTimeUtc) {
      return;
    }
    decidedForRef.current = cartCreatedDateTimeUtc;

    // The decision is cart-scoped, so a cart switch has to clear the previous cart's before
    // re-deciding: otherwise its treatment value and RUM tag bleed into the new cart while
    // bucketing is in flight, and permanently if the new lookup errors out or is ineligible.
    setIsBackstopTreatment(false);
    datadogRum.removeGlobalContextProperty(`experiments.${BACKSTOP_EXPERIMENT}`);

    // Pre-launch (or dateless) carts are excluded: control, no bucketing, no RUM tag. Deliberately
    // NOT cached — the SplitTest cookie is browser-scoped but eligibility is cart-scoped, so a cached
    // control could bleed into a later eligible cart. The date check is cheap, so we recompute it.
    if (!isEligible(cartCreatedDateTimeUtc)) {
      return;
    }

    const decidedFor = cartCreatedDateTimeUtc;
    GetAssignedBucket({ experimentName: BACKSTOP_EXPERIMENT, cacheMinutes: 15 }).then(result => {
      // A superseding cart switch invalidates this in-flight lookup.
      if (decidedForRef.current !== decidedFor) return;
      // A SplitTest outage resolves to control; skip it so failed lookups aren't counted into the
      // control cohort in RUM — the guard still safely defaults to control behavior.
      if ('hasErrors' in result && result.hasErrors) return;
      const bucket = result.assignedBucket ?? 'control';
      setIsBackstopTreatment(bucket === 'treatment');
      // Server-sourced experiment — not in ExperimentProvider, requires manual RUM context
      datadogRum.setGlobalContextProperty(`experiments.${BACKSTOP_EXPERIMENT}`, bucket);
    });
  }, [cartCreatedDateTimeUtc]);

  return (
    <BackstopContext.Provider value={{ isBackstopTreatment }}>{children}</BackstopContext.Provider>
  );
};

export default BackstopContext;
export {
  BackstopProvider,
  BACKSTOP_EXPERIMENT_START_DATE,
  BACKSTOP_START_DATE_IS_TEST_PLACEHOLDER
};
